<template>
  <section
    v-if="anyVisible"
    class="dash-comms-trio"
    :class="{ 'dash-comms-trio--busy': loading }"
  >
    <b-row class="dash-comms-trio__row match-height">
      <!-- News -->
      <b-col
        v-if="canNews"
        cols="12"
        lg="4"
        class="dash-comms-trio__col mb-1"
      >
        <article class="dash-comms-card dash-comms-card--news h-100">
          <header class="dash-comms-card__head">
            <div class="dash-comms-card__brand">
              <span class="dash-comms-card__icon dash-comms-card__icon--news">
                <feather-icon icon="RssIcon" size="18" />
              </span>
              <div>
                <h3 class="dash-comms-card__title mb-0">
                  {{ $t("News Feed") }}
                </h3>
                <p class="dash-comms-card__sub mb-0">
                  {{
                    newsModeToday
                      ? $t("Posted today")
                      : $t("Latest announcements")
                  }}
                </p>
              </div>
            </div>
            <b-button
              variant="flat-primary"
              size="sm"
              class="dash-comms-card__link btn-icon"
              :to="{ name: 'news-feed' }"
            >
              <feather-icon icon="ArrowRightIcon" size="16" />
            </b-button>
          </header>

          <div class="dash-comms-card__body">
            <div v-if="loadingNews" class="dash-comms-card__skeleton">
              <div
                v-for="n in 3"
                :key="'nsk-' + n"
                class="dash-comms-sk-tile"
              />
            </div>
            <ul v-else-if="newsDisplay.length" class="dash-comms-feed">
              <li
                v-for="item in newsDisplay"
                :key="'news-' + item.id"
                class="dash-comms-news"
                role="button"
                tabindex="0"
                @click="openRoute('news-feed')"
                @keyup.enter="openRoute('news-feed')"
              >
                <div
                  v-if="newsThumb(item)"
                  class="dash-comms-news__thumb"
                >
                  <img
                    :src="newsThumb(item)"
                    alt=""
                    loading="lazy"
                    @error="onNewsImageError($event, item.id)"
                  />
                  <span
                    v-if="newsMediaCount(item) > 1"
                    class="dash-comms-news__count"
                  >
                    +{{ newsMediaCount(item) - 1 }}
                  </span>
                </div>
                <div
                  v-else
                  class="dash-comms-news__thumb dash-comms-news__thumb--placeholder"
                >
                  <feather-icon icon="ImageIcon" size="22" />
                </div>
                <div class="dash-comms-news__content">
                  <time class="dash-comms-news__date">{{
                    formatListDate(newsItemDate(item))
                  }}</time>
                  <p class="dash-comms-news__title mb-0">
                    {{ stripHtml(item.caption) }}
                  </p>
                  <span v-if="item.name" class="dash-comms-news__by">{{
                    item.name
                  }}</span>
                </div>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="16"
                  class="dash-comms-news__chev"
                />
              </li>
            </ul>
            <div v-else class="dash-comms-empty">
              <feather-icon icon="InboxIcon" size="22" />
              <span>{{ $t("No news for today") }}</span>
            </div>
          </div>
        </article>
      </b-col>

      <!-- Circulars -->
      <b-col
        v-if="canCirculars"
        cols="12"
        lg="4"
        class="dash-comms-trio__col mb-1"
      >
        <article class="dash-comms-card dash-comms-card--circular h-100">
          <header class="dash-comms-card__head">
            <div class="dash-comms-card__brand">
              <span
                class="dash-comms-card__icon dash-comms-card__icon--circular"
              >
                <feather-icon icon="FileTextIcon" size="18" />
              </span>
              <div>
                <h3 class="dash-comms-card__title mb-0">
                  {{ $t("Circulars") }}
                </h3>
                <p class="dash-comms-card__sub mb-0">
                  {{
                    circularModeToday
                      ? $t("Active today")
                      : $t("Latest notices")
                  }}
                </p>
              </div>
            </div>
            <b-button
              variant="flat-primary"
              size="sm"
              class="dash-comms-card__link btn-icon"
              :to="{ name: 'circulars' }"
            >
              <feather-icon icon="ArrowRightIcon" size="16" />
            </b-button>
          </header>

          <div class="dash-comms-card__body">
            <div v-if="loadingCirculars" class="dash-comms-card__skeleton">
              <div
                v-for="n in 3"
                :key="'csk-' + n"
                class="dash-comms-sk-tile"
              />
            </div>
            <ul v-else-if="circularDisplay.length" class="dash-comms-feed">
              <li
                v-for="item in circularDisplay"
                :key="'circ-' + item.id"
                class="dash-comms-circ"
                role="button"
                tabindex="0"
                @click="openRoute('circulars')"
                @keyup.enter="openRoute('circulars')"
              >
                <div class="dash-comms-circ__icon-wrap">
                  <feather-icon icon="BellIcon" size="18" />
                </div>
                <div class="dash-comms-circ__main">
                  <div class="dash-comms-circ__top">
                    <b-badge
                      :variant="circularBadgeVariant(item.circularFor)"
                      class="dash-comms-circ__badge text-capitalize"
                    >
                      {{ circularAudienceLabel(item.circularFor) }}
                    </b-badge>
                    <span
                      v-if="circularActiveToday(item)"
                      class="dash-comms-circ__live"
                    >
                      {{ $t("Live") }}
                    </span>
                  </div>
                  <p class="dash-comms-circ__title mb-0">
                    {{ item.title || $t("Untitled") }}
                  </p>
                  <p
                    v-if="circularDescription(item)"
                    class="dash-comms-circ__desc mb-0"
                  >
                    {{ circularDescription(item) }}
                  </p>
                  <div class="dash-comms-circ__meta">
                    <span class="dash-comms-circ__dates">{{
                      circularDateRange(item)
                    }}</span>
                    <span
                      v-if="circularCampusLabel(item)"
                      class="dash-comms-circ__campus"
                    >
                      {{ circularCampusLabel(item) }}
                    </span>
                  </div>
                </div>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="16"
                  class="dash-comms-circ__chev"
                />
              </li>
            </ul>
            <div v-else class="dash-comms-empty">
              <feather-icon icon="InboxIcon" size="22" />
              <span>{{ $t("No circulars for today") }}</span>
            </div>
          </div>
        </article>
      </b-col>

      <!-- Resources -->
      <b-col
        v-if="canResources"
        cols="12"
        lg="4"
        class="dash-comms-trio__col mb-1"
      >
        <article class="dash-comms-card dash-comms-card--resource h-100">
          <header class="dash-comms-card__head">
            <div class="dash-comms-card__brand">
              <span
                class="dash-comms-card__icon dash-comms-card__icon--resource"
              >
                <feather-icon icon="FolderIcon" size="18" />
              </span>
              <div>
                <h3 class="dash-comms-card__title mb-0">
                  {{ $t("Resources") }}
                </h3>
                <p class="dash-comms-card__sub mb-0">
                  {{
                    resourceModeToday
                      ? $t("Added today")
                      : $t("Latest uploads")
                  }}
                </p>
              </div>
            </div>
            <b-button
              variant="flat-primary"
              size="sm"
              class="dash-comms-card__link btn-icon"
              :to="{ name: 'resource' }"
            >
              <feather-icon icon="ArrowRightIcon" size="16" />
            </b-button>
          </header>

          <div class="dash-comms-card__body">
            <div v-if="loadingResources" class="dash-comms-card__skeleton">
              <div
                v-for="n in 4"
                :key="'rsk-' + n"
                class="dash-comms-sk-tile"
              />
            </div>
            <ul v-else-if="resourceDisplay.length" class="dash-comms-feed">
              <li
                v-for="item in resourceDisplay"
                :key="'res-' + item.id"
                class="dash-comms-res"
                role="button"
                tabindex="0"
                @click="openRoute('resource')"
                @keyup.enter="openRoute('resource')"
              >
                <div
                  class="dash-comms-res__type"
                  :class="'dash-comms-res__type--' + resourceTypeKey(item)"
                >
                  <feather-icon
                    :icon="resourceTypeIcon(item)"
                    size="17"
                  />
                </div>
                <div class="dash-comms-res__main">
                  <div class="dash-comms-res__top">
                    <p class="dash-comms-res__title mb-0">
                      {{ item.title || $t("Untitled") }}
                    </p>
                    <b-badge
                      v-if="item.isPublished"
                      variant="light-success"
                      class="dash-comms-res__pub"
                    >
                      {{ $t("Published") }}
                    </b-badge>
                  </div>
                  <div class="dash-comms-res__chips">
                    <span
                      v-if="resourceClassLabel(item)"
                      class="dash-comms-chip dash-comms-chip--class"
                    >
                      {{ resourceClassLabel(item) }}
                    </span>
                    <span
                      v-if="resourceSectionLabel(item)"
                      class="dash-comms-chip dash-comms-chip--sec"
                    >
                      {{ resourceSectionLabel(item) }}
                    </span>
                    <span
                      v-if="resourceSubjectLabel(item)"
                      class="dash-comms-chip dash-comms-chip--sub"
                    >
                      {{ resourceSubjectLabel(item) }}
                    </span>
                  </div>
                  <div class="dash-comms-res__foot">
                    <time>{{
                      formatListDate(resourceItemDate(item))
                    }}</time>
                    <span
                      v-if="resourceAttachmentCount(item)"
                      class="dash-comms-res__files"
                    >
                      <feather-icon icon="PaperclipIcon" size="12" />
                      {{ resourceAttachmentCount(item) }}
                    </span>
                  </div>
                </div>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="16"
                  class="dash-comms-res__chev"
                />
              </li>
            </ul>
            <div v-else class="dash-comms-empty">
              <feather-icon icon="InboxIcon" size="22" />
              <span>{{ $t("No resources for today") }}</span>
            </div>
          </div>
        </article>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { BRow, BCol, BButton, BBadge } from "bootstrap-vue";
import { mapActions } from "vuex";
import {
  normalizeDocAttachment,
  getMediaPreviewUrl,
  isImageUrl,
} from "@/utils/gdriveUpload";

const NEWS_LIMIT = 3;
const CIRCULAR_LIMIT = 3;
const RESOURCE_LIMIT = 4;
const newsDateKeys = ["date", "postedDate", "addedOn", "postedOn"];

export default {
  name: "DashboardCommsTrio",
  components: { BRow, BCol, BButton, BBadge },
  data() {
    return {
      loadingNews: false,
      loadingCirculars: false,
      loadingResources: false,
      loadingLookups: false,
      newsItems: [],
      circularItems: [],
      resourceItems: [],
      classes: [],
      classesOptions: [],
      subjects: [],
      newsImageFailed: {},
    };
  },
  computed: {
    loading() {
      return (
        this.loadingNews ||
        this.loadingCirculars ||
        this.loadingResources ||
        this.loadingLookups
      );
    },
    canNews() {
      return this.hasViewRight("news-feed");
    },
    canCirculars() {
      return this.hasViewRight("circulars");
    },
    canResources() {
      return this.hasViewRight("resource");
    },
    anyVisible() {
      return this.canNews || this.canCirculars || this.canResources;
    },
    newsPick() {
      return this.pickDisplayList(
        this.newsItems,
        NEWS_LIMIT,
        (item) => this.newsItemDate(item),
        (item) => {
          const d = this.parseDate(this.newsItemDate(item));
          return d ? this.isSameCalendarDay(d, this.today()) : false;
        }
      );
    },
    newsDisplay() {
      return this.newsPick.items;
    },
    newsModeToday() {
      return this.newsPick.usedToday;
    },
    circularPick() {
      return this.pickDisplayList(
        this.circularItems,
        CIRCULAR_LIMIT,
        (item) => item.dtFrom,
        (item) => this.circularActiveToday(item)
      );
    },
    circularDisplay() {
      return this.circularPick.items;
    },
    circularModeToday() {
      return this.circularPick.usedToday;
    },
    resourcePick() {
      return this.pickDisplayList(
        this.resourceItems,
        RESOURCE_LIMIT,
        (item) => this.resourceItemDate(item),
        (item) => {
          const d = this.parseDate(this.resourceItemDate(item));
          return d ? this.isSameCalendarDay(d, this.today()) : false;
        }
      );
    },
    resourceDisplay() {
      return this.resourcePick.items;
    },
    resourceModeToday() {
      return this.resourcePick.usedToday;
    },
  },
  mounted() {
    this.refreshAll();
  },
  activated() {
    this.refreshAll();
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    openRoute(name) {
      this.$router.push({ name }).catch(() => {});
    },

    hasViewRight(routeName) {
      const rights = this.$store.state.rights?.[routeName];
      if (!rights) return true;
      return !!rights.view;
    },

    today() {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    },

    parseDate(raw) {
      if (!raw) return null;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    },

    isSameCalendarDay(a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    },

    newsItemDate(item) {
      return this.itemSortDate(item, newsDateKeys);
    },

    resourceItemDate(item) {
      return (
        item.date ??
        item.createdAt ??
        item.createdOn ??
        item.addedOn ??
        null
      );
    },

    itemSortDate(item, keys) {
      for (let i = 0; i < keys.length; i += 1) {
        const v = item[keys[i]];
        if (v) return v;
      }
      return null;
    },

    circularActiveToday(item) {
      const from = this.parseDate(item.dtFrom);
      if (!from) return false;
      let to = this.parseDate(item.dtTo);
      if (!to) to = from;
      const today = this.today();
      return (
        from.getTime() <= today.getTime() && to.getTime() >= today.getTime()
      );
    },

    pickDisplayList(items, limit, dateKeyFn, isTodayFn) {
      const sorted = [...(items || [])].sort((a, b) => {
        const da = this.parseDate(dateKeyFn(a));
        const db = this.parseDate(dateKeyFn(b));
        const ta = da ? da.getTime() : 0;
        const tb = db ? db.getTime() : 0;
        return tb - ta;
      });
      const todayRows = sorted.filter((row) => isTodayFn(row));
      const usedToday = todayRows.length > 0;
      const pool = usedToday ? todayRows : sorted;
      return {
        usedToday,
        items: pool.slice(0, limit),
      };
    },

    stripHtml(html) {
      const t = String(html || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (t.length <= 100) return t;
      return `${t.slice(0, 99)}…`;
    },

    formatListDate(raw) {
      const d = this.parseDate(raw);
      if (!d) return "—";
      if (this.isSameCalendarDay(d, this.today())) return this.$t("Today");
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    },

    formatShortDate(raw) {
      const d = raw ? new Date(raw) : null;
      if (!d || Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    newsMediaUrls(item) {
      const m = item?.media;
      if (!m) return [];
      return String(m)
        .split(",")
        .map((u) => u.trim())
        .filter(Boolean)
        .map((u) => normalizeDocAttachment(u));
    },

    newsThumb(item) {
      if (this.newsImageFailed[item.id]) return null;
      const urls = this.newsMediaUrls(item);
      for (let i = 0; i < urls.length; i += 1) {
        const u = urls[i];
        if (isImageUrl(u) || u) {
          return getMediaPreviewUrl(u);
        }
      }
      return null;
    },

    newsMediaCount(item) {
      return this.newsMediaUrls(item).length;
    },

    onNewsImageError(_ev, id) {
      this.$set(this.newsImageFailed, id, true);
    },

    circularBadgeVariant(forWho) {
      if (forWho === "all") return "light-primary";
      if (forWho === "staff") return "light-info";
      if (forWho === "student") return "light-success";
      return "light-secondary";
    },

    circularAudienceLabel(forWho) {
      if (forWho === "all") return this.$t("All");
      if (forWho === "staff") return this.$t("Staff");
      if (forWho === "student") return this.$t("Students");
      return forWho || this.$t("General");
    },

    circularDescription(item) {
      const d = String(item.description || item.metaData || "").trim();
      if (!d || d.length < 3) return "";
      const plain = d.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      if (plain.length <= 72) return plain;
      return `${plain.slice(0, 71)}…`;
    },

    circularDateRange(item) {
      const a = this.formatShortDate(item.dtFrom);
      const b = this.formatShortDate(item.dtTo);
      if (a && b && a !== b) return `${a} – ${b}`;
      return a || b || "—";
    },

    circularCampusLabel(item) {
      const raw = item.metaCampusName || item.campusName || "";
      if (!raw) return "";
      const parts = String(raw)
        .split(/[,|]/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (!parts.length) return "";
      if (parts.length === 1) return parts[0];
      return `${parts[0]} +${parts.length - 1}`;
    },

    getClassName(id) {
      const obj = this.classesOptions.find((el) => el.id == id);
      return obj ? obj.name : "";
    },

    getSubName(id) {
      const obj = this.subjects.find((el) => el.id == id);
      return obj ? obj.subject : "";
    },

    getSecName(clsID, secId) {
      const row = this.classes.find((el) => el.cls.id == clsID);
      if (!row) return "";
      const sec = row.sections.find((el) => el.id == secId);
      return sec ? sec.section : "";
    },

    looksLikeId(val) {
      if (val === null || val === undefined || val === "") return true;
      const s = String(val).trim();
      return /^\d+$/.test(s);
    },

    resourceClassLabel(item) {
      const fromLookup = this.getClassName(item.cID ?? item.clsID);
      if (fromLookup) return fromLookup;
      const name = item.className || item.class;
      if (name && !this.looksLikeId(name)) return name;
      return "";
    },

    resourceSectionLabel(item) {
      const fromLookup = this.getSecName(
        item.cID ?? item.clsID,
        item.section
      );
      if (fromLookup) return fromLookup;
      const name = item.sectionName || item.secName;
      if (name && !this.looksLikeId(name)) return name;
      return "";
    },

    resourceSubjectLabel(item) {
      const fromLookup = this.getSubName(item.subID ?? item.subjectId);
      if (fromLookup) return fromLookup;
      const name = item.subjectName || item.subject;
      if (name && !this.looksLikeId(name)) return name;
      return "";
    },

    resourceTypeKey(item) {
      const t = String(item.type || "document").toLowerCase();
      if (["video", "audio", "presentation"].includes(t)) return t;
      return "document";
    },

    resourceTypeIcon(item) {
      const map = {
        video: "VideoIcon",
        audio: "MicIcon",
        presentation: "MonitorIcon",
        document: "FileTextIcon",
      };
      return map[this.resourceTypeKey(item)] || "FileTextIcon";
    },

    resourceAttachmentCount(item) {
      const a = item.attachments;
      if (Array.isArray(a)) return a.length;
      if (typeof a === "string" && a.trim()) {
        return a.split(",").filter(Boolean).length;
      }
      return 0;
    },

    async loadResourceLookups() {
      if (!this.canResources) return;
      this.loadingLookups = true;
      try {
        const [classesRes, subjectsRes] = await Promise.all([
          this.get({
            url: `${this.$store.state.domain}myclasses/LoadClasses?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&dID=0`,
            token: this.$store.state.userData.token,
          }),
          this.get({
            url: `${this.$store.state.domain}Subjects?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
            token: this.$store.state.userData.token,
          }),
        ]);
        this.classes = Array.isArray(classesRes) ? classesRes : [];
        this.classesOptions = this.classes.map((el) => el.cls);
        this.subjects = Array.isArray(subjectsRes) ? subjectsRes : [];
      } catch (e) {
        console.error("Dashboard resource lookups failed:", e);
      } finally {
        this.loadingLookups = false;
      }
    },

    async refreshAll() {
      const jobs = [];
      if (this.canNews) jobs.push(this.loadNews());
      if (this.canCirculars) jobs.push(this.loadCirculars());
      if (this.canResources) {
        jobs.push(this.loadResourceLookups());
        jobs.push(this.loadResources());
      }
      await Promise.all(jobs);
    },

    async loadNews() {
      this.loadingNews = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "NewsFeed/GetFeed?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        if (res && res.status === "success" && Array.isArray(res.data)) {
          this.newsItems = res.data;
        } else if (Array.isArray(res)) {
          this.newsItems = res;
        } else if (Array.isArray(res?.data)) {
          this.newsItems = res.data;
        } else {
          this.newsItems = [];
        }
      } catch (e) {
        console.error("Dashboard news load failed:", e);
        this.newsItems = [];
      } finally {
        this.loadingNews = false;
      }
    },

    async loadCirculars() {
      this.loadingCirculars = true;
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: String(this.$store.state.userData.cId),
          page: "1",
          pageSize: "40",
        });
        const res = await this.get({
          url:
            this.$store.state.domain +
            "Circulars/LoadAllCirculars?" +
            params.toString(),
          token: this.$store.state.userData.token,
          returnData: true,
        });
        if (res && Array.isArray(res.data)) {
          this.circularItems = res.data;
        } else if (Array.isArray(res)) {
          this.circularItems = res;
        } else {
          this.circularItems = [];
        }
      } catch (e) {
        console.error("Dashboard circulars load failed:", e);
        this.circularItems = [];
      } finally {
        this.loadingCirculars = false;
      }
    },

    async loadResources() {
      this.loadingResources = true;
      try {
        const res = await this.post({
          url: `${this.$store.state.domain}Resources/LoadData`,
          body: {
            db: this.$store.state.userData.db,
            cID: this.$store.state.userData.cId,
            clsID: 0,
            sections: [],
            tID: 0,
            page: 1,
            pageSize: 30,
            search: "",
          },
          token: this.$store.state.userData.token,
          allResponse: true,
          ignoreMessage: true,
        });
        let rows = [];
        if (Array.isArray(res?.data)) rows = res.data;
        else if (Array.isArray(res?.data?.data)) rows = res.data.data;
        else if (Array.isArray(res)) rows = res;
        this.resourceItems = rows;
      } catch (e) {
        console.error("Dashboard resources load failed:", e);
        this.resourceItems = [];
      } finally {
        this.loadingResources = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dash-comms-trio {
  margin: 0.85rem 0 0.35rem;

  &__row {
    align-items: stretch;
  }

  &__col {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
}

.dash-comms-card {
  display: flex;
  flex-direction: column;
  min-height: 320px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(16px) saturate(1.12);
  -webkit-backdrop-filter: blur(16px) saturate(1.12);
  border: 1px solid rgba(115, 103, 240, 0.1);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.88) inset,
    0 14px 36px rgba(115, 103, 240, 0.07);
  overflow: hidden;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.65rem;
    padding: 1rem 1rem 0.75rem;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    border-bottom: 1px solid rgba(115, 103, 240, 0.06);
  }

  &__brand {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-width: 0;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 14px;
    flex-shrink: 0;
    color: #fff;

    &--news {
      background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
      box-shadow: 0 6px 16px rgba(115, 103, 240, 0.35);
    }

    &--circular {
      background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
      box-shadow: 0 6px 16px rgba(14, 165, 233, 0.28);
    }

    &--resource {
      background: linear-gradient(135deg, #28c76f 0%, #16a34a 100%);
      box-shadow: 0 6px 16px rgba(40, 199, 111, 0.28);
    }
  }

  &__title {
    font-size: 1rem;
    font-weight: 800;
    color: #2f2b3d;
    letter-spacing: -0.02em;
  }

  &__sub {
    font-size: 0.75rem;
    color: #6e6b7b;
    margin-top: 0.12rem;
  }

  &__link {
    flex-shrink: 0;
    border-radius: 10px !important;
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0.55rem 0.75rem 0.85rem;
    display: flex;
    flex-direction: column;
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }
}

.dash-comms-sk-tile {
  height: 4.5rem;
  border-radius: 16px;
  background: linear-gradient(
    90deg,
    rgba(235, 233, 241, 0.9) 25%,
    rgba(248, 247, 252, 0.95) 50%,
    rgba(235, 233, 241, 0.9) 75%
  );
  background-size: 200% 100%;
  animation: dashCommsShimmer 1.2s infinite;
}

@keyframes dashCommsShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.dash-comms-feed {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow: auto;
  max-height: 380px;
  overscroll-behavior: contain;
}

/* News tiles with image */
.dash-comms-news {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.45rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(115, 103, 240, 0.18);
    box-shadow: 0 8px 22px rgba(115, 103, 240, 0.1);
  }

  &__thumb {
    position: relative;
    width: 4.25rem;
    height: 4.25rem;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
    background: rgba(115, 103, 240, 0.08);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #7367f0;
    }
  }

  &__count {
    position: absolute;
    right: 4px;
    bottom: 4px;
    font-size: 0.6rem;
    font-weight: 800;
    padding: 0.1rem 0.35rem;
    border-radius: 6px;
    background: rgba(15, 23, 42, 0.65);
    color: #fff;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__date {
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7367f0;
  }

  &__title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #2f2b3d;
    line-height: 1.35;
    margin-top: 0.12rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__by {
    display: block;
    margin-top: 0.15rem;
    font-size: 0.68rem;
    color: #a8aaae;
  }

  &__chev {
    flex-shrink: 0;
    color: #c4c4cc;
  }
}

/* Circular cards */
.dash-comms-circ {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem 0.55rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(14, 165, 233, 0.1);
    border-color: rgba(14, 165, 233, 0.15);
  }

  &__icon-wrap {
    width: 2.35rem;
    height: 2.35rem;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #0284c7;
    background: rgba(14, 165, 233, 0.12);
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__top {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-bottom: 0.25rem;
  }

  &__badge {
    font-size: 0.65rem;
    font-weight: 700;
  }

  &__live {
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #28c76f;
    padding: 0.12rem 0.4rem;
    border-radius: 999px;
    background: rgba(40, 199, 111, 0.12);
  }

  &__title {
    font-size: 0.8125rem;
    font-weight: 800;
    color: #2f2b3d;
    line-height: 1.3;
  }

  &__desc {
    font-size: 0.72rem;
    color: #6e6b7b;
    margin-top: 0.2rem;
    line-height: 1.35;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.65rem;
    margin-top: 0.35rem;
    font-size: 0.68rem;
    color: #a8aaae;
  }

  &__dates {
    font-weight: 600;
  }

  &__campus {
    font-weight: 600;
    color: #7367f0;
  }

  &__chev {
    flex-shrink: 0;
    margin-top: 0.35rem;
    color: #c4c4cc;
  }
}

/* Resource rows */
.dash-comms-res {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.5rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(40, 199, 111, 0.1);
    border-color: rgba(40, 199, 111, 0.18);
  }

  &__type {
    width: 2.35rem;
    height: 2.35rem;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;

    &--document {
      background: linear-gradient(135deg, #7367f0, #6254e8);
    }
    &--video {
      background: linear-gradient(135deg, #ef4444, #dc2626);
    }
    &--audio {
      background: linear-gradient(135deg, #f59e0b, #d97706);
    }
    &--presentation {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
    }
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.35rem;
  }

  &__title {
    font-size: 0.8125rem;
    font-weight: 800;
    color: #2f2b3d;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__pub {
    flex-shrink: 0;
    font-size: 0.6rem;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    margin-top: 0.35rem;
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-top: 0.3rem;
    font-size: 0.68rem;
    font-weight: 600;
    color: #a8aaae;

    time {
      color: #7367f0;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      font-size: 0.62rem;
    }
  }

  &__files {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
  }

  &__chev {
    flex-shrink: 0;
    color: #c4c4cc;
  }
}

.dash-comms-chip {
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.15rem 0.45rem;
  border-radius: 8px;

  &--class {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  &--sec {
    background: rgba(14, 165, 233, 0.12);
    color: #0284c7;
  }

  &--sub {
    background: rgba(40, 199, 111, 0.12);
    color: #16a34a;
  }
}

.dash-comms-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 1.5rem 0.75rem;
  text-align: center;
  color: #a8aaae;
  font-size: 0.8125rem;
  font-weight: 600;
}

@media (max-width: 991px) {
  .dash-comms-card {
    min-height: 280px;
  }
}
</style>
